export const CANCELLATION_REASONS = [
  'Non-viable booking',
  'Cancelled within cooling off period',
  'Pricing glitch',
  'Availability glitch',
  'Job Loss',
  'Medical',
  'Bereavement',
  'Change of Mind',
  'Major world event',
  'Liquidation of carrier',
  'Cancellation of customer event',
  'No longer travelling',
  'Financial Hardship',
  'Customer Fraud Failure',
] as const

export type CancellationReason = (typeof CANCELLATION_REASONS)[number]

/** A consolidator's refund-fee rule, keyed by `${owningPcc} (${ticketingPcc})`. */
export interface ConsolidatorRefundRule {
  /** Flat per-pax refund fee. */
  ADT: number
  CHD: number
  INF: number
  currency: string
  /**
   * Fraction of the ticket's issuance commission the consolidator claws back on refund.
   * Defaults to 1.0 (full). e.g. P0IA reclaims only 85%.
   */
  commissionClawbackRate?: number
  /**
   * When true, GST is added to the flat fee for wholly AU/NZ-domestic bookings (AU 10% / NZ 15%).
   * International itineraries are unaffected.
   */
  gstOnDomestic?: boolean
  /**
   * When true, the consolidator keeps the booking's untied PROCFE processing fee on refund, so it
   * comes off the expected refund like the flat fee. DTT does (PLT2743397: 318.20 ticket + 5.00
   * PROCFE paid, 293.20 refunded = 318.20 − 25 flat fee).
   */
  keepsProcessingFees?: boolean
}

export const CONSOLIDATOR_REFUND_FEE: Record<string, ConsolidatorRefundRule> = {
  'W8QK (W8QK)': { ADT: 25, CHD: 25, INF: 25, currency: 'AUD' },
  'W8QK (P0IA)': { ADT: 15, CHD: 15, INF: 15, currency: 'AUD', commissionClawbackRate: 0.85, gstOnDomestic: true },
  'W8QK (4ZTK)': { ADT: 10, CHD: 10, INF: 10, currency: 'GBP' },
  'IG5K (37S2)': { ADT: 10, CHD: 10, INF: 10, currency: 'GBP' },
  '4WBJ (UT8I)': { ADT: 25, CHD: 25, INF: 25, currency: 'USD', keepsProcessingFees: true },
  // W3OM is DTT's NDC IPCC — same consolidator as 4WBJ, so the same USD 25 fee. Both ticketing
  // pairs are listed because the issuing PCC on a W3OM NDC ticket is unconfirmed: DTT's robot works
  // the order off A5C6 (queue 490), while its classic tickets stamp UT8I. Whichever the first real
  // ticket shows, the other can go — a missing pair is a silent 0, not an error. (BIR-166)
  'W3OM (UT8I)': { ADT: 25, CHD: 25, INF: 25, currency: 'USD', keepsProcessingFees: true },
  'W3OM (A5C6)': { ADT: 25, CHD: 25, INF: 25, currency: 'USD', keepsProcessingFees: true },
  'AR4L (QQ3J)': { ADT: 15, CHD: 15, INF: 15, currency: 'USD' },
  // NZ: owningPcc W8QK / ticketingPcc C0I9 (real ticket — see MLRILI fixture), not 1AYL despite it
  // being the NZ POS. A key miss silently yields a 0 consolidator fee (deriveConsolidatorFee).
  'W8QK (C0I9)': { ADT: 15, CHD: 15, INF: 15, currency: 'NZD' },
}

export const ELIGIBILITY_GATES = [
  'lcc-booking',
  'airline-control',
  'access-restricted',
  'partial-data',
  'foreign-currency',
  'non-auto-carrier',
  'unverifiable-penalty-clause',
  'manual-deductions',
] as const
export type RefundEligibilityGate = (typeof ELIGIBILITY_GATES)[number]

/**
 * Carrier surcharges withheld regardless of timing (government/airport taxes on unused coupons stay
 * refundable). The withholding floor when we estimate a refund ourselves; the automated path trusts
 * Sabre's `refundTotals` as-is instead.
 *
 * "Always" is the floor for the penalty-bearing case, not a property of the codes: YQ/YR are
 * carrier-imposed, so they follow the fare's own refund conditions. A confirmed 0 penalty lifts the
 * floor — see estimatedNonRefundableTaxes.
 */
export const ALWAYS_NON_REFUNDABLE_TAX_CODES = new Set(['YQ', 'YR'])

/** Coupon status codes for a coupon still open for use (O/I open, P printed), as the booking read reports them. */
export const COUPON_OPEN_CODES = new Set(['O', 'I', 'P'])
